import { Badge, FormStatus, P, Space } from '@dnb/eufemia';
import { Field, Form } from '@dnb/eufemia/extensions/forms';
import type { ApiClassification } from '@portals/shared/portal/ApiDto';
import { ApiType, ApiWithStatusAndScopesDto } from '@portals/shared/portal/ApiDto';
import type { JSX } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { updateApiFields } from '@/api/api';
import type { ApiUpdateRequest } from '@/openapi/schema';
import { FormCancelButton } from '@/pages/profile/apps/application/ApiProviderPage/components/FormCancelButton';
import { FormEditButton } from '@/pages/profile/apps/application/ApiProviderPage/components/FormEditButton';
import FormSubmitButton from '@/pages/profile/apps/application/ApiProviderPage/components/FormSubmitButton';
import { SwitchContainerMode } from '@/types/SwitchContainerModeTypes';
import { convertCamelCaseToTitleCase } from '@/utils';

import style from './index.module.css';

type ApiDetailsProps = Pick<
  ApiWithStatusAndScopesDto,
  'classification' | 'stage' | 'description' | 'slug' | 'name' | 'type'
> & {
  isTeamAdmin?: boolean;
  onUpdate?: (updatedApi: Partial<ApiWithStatusAndScopesDto>) => void;
};

const CLASSIFICATION_OPTIONS: Array<{ title: string; value: Uppercase<ApiClassification> }> = [
  { title: 'System', value: 'SYSTEM' },
  { title: 'Domain', value: 'DOMAIN' },
  { title: 'Experience', value: 'EXPERIENCE' },
  { title: 'Public', value: 'PUBLIC' },
  { title: 'Partner', value: 'PARTNER' },
  { title: 'Technical', value: 'TECHNICAL' },
  { title: 'Legacy', value: 'LEGACY' },
];

export function ApiDetailsSection({
  classification,
  stage,
  description,
  slug,
  name,
  type,
  isTeamAdmin = false,
  onUpdate,
}: ApiDetailsProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const switchContainerModeRef = useRef<SwitchContainerMode>(undefined);

  const isRestApi = type === ApiType.REST;

  const formId = `api-details-${slug}`;
  const { data: formData, set } = Form.useData(formId);
  const { hasErrors } = Form.useValidation(formId);

  const originalData = useMemo(
    () => ({
      details: {
        name,
        classification: classification.toUpperCase(),
        description,
      },
    }),
    [classification, description, name],
  );

  const hasChanged = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const handleCancel = useCallback(() => {
    set(originalData);
    setErrorMessage(null);
  }, [set, originalData]);

  const handleSubmit = useCallback(
    async (data: { details: { name: string; classification: string; description: string | null } }) => {
      setErrorMessage(null);

      const requestBody: ApiUpdateRequest = {
        ...(data.details.name !== name && { name: data.details.name }),
        ...(data.details.classification !== classification.toUpperCase() && {
          classification: data.details.classification as ApiUpdateRequest['classification'],
        }),
        ...(data.details.description !== description && { description: data.details.description || '' }),
      };
      const response = await updateApiFields(slug, requestBody);

      if (response.error || response.response?.status > 400) {
        setErrorMessage(response.error?.detail || 'Failed to update API details');
        return;
      }

      if (response.data) {
        const updatedData: Partial<ApiWithStatusAndScopesDto> = {
          name: response.data.name,
          classification: response.data.classification.toLowerCase() as ApiWithStatusAndScopesDto['classification'],
          description: response.data.description,
        };
        onUpdate?.(updatedData);
        switchContainerModeRef.current?.('view');
      }
    },
    [classification, description, name, onUpdate, slug],
  );

  return (
    <Form.Handler
      defaultData={{
        details: {
          name,
          classification: classification.toUpperCase(),
          description,
        },
      }}
      id={formId}
      onSubmit={handleSubmit}
    >
      <Form.Section>
        <Form.Section.ViewContainer
          onEdit={() => {
            setErrorMessage(null);
          }}
        >
          <Space bottom="small" className={style['ApiDetails-CardRow']}>
            <P bold className={style['ApiDetails-Label']}>
              Name
            </P>
            <P>{name}</P>
          </Space>

          <Space bottom="small" className={style['ApiDetails-CardRow']}>
            <P bold className={style['ApiDetails-Label']}>
              Classification
            </P>
            <Badge content={convertCamelCaseToTitleCase(classification)} status="positive" />
          </Space>

          <Space bottom="small" className={style['ApiDetails-CardRow']}>
            <P bold className={style['ApiDetails-Label']}>
              Stage
            </P>
            <Badge content={convertCamelCaseToTitleCase(stage)} status="positive" />
          </Space>

          <Space bottom="small" className={style['ApiDetails-CardRow']}>
            <P bold className={style['ApiDetails-Label']}>
              Slug
            </P>
            <P>{slug}</P>
          </Space>

          <Space className={style['ApiDetails-CardRow']}>
            <P bold className={style['ApiDetails-Label']}>
              Description
            </P>
            <P>{description ?? 'No description provided'}</P>
          </Space>
          <Form.Section.Toolbar>
            <FormEditButton
              disabled={!isTeamAdmin || !isRestApi}
              disabledTooltip={
                isTeamAdmin ? (
                  isRestApi ? undefined : (
                    <P>Only REST APIs can be edited. Events are managed by Eventhub team</P>
                  )
                ) : (
                  <P>Only team admins have access to edit</P>
                )
              }
              switchContainerModeRef={switchContainerModeRef}
            />
          </Form.Section.Toolbar>
        </Form.Section.ViewContainer>

        <Form.Section.EditContainer stretch>
          <Field.String
            label="Name"
            maxLength={255}
            minLength={1}
            path="/details/name"
            required
            top="small"
            width="stretch"
          />

          <Field.Selection
            label="Classification"
            path="/details/classification"
            top="medium"
            variant="dropdown"
            width="stretch"
          >
            {CLASSIFICATION_OPTIONS.map((option) => (
              <Field.Option key={option.value} title={option.title} value={option.value} />
            ))}
          </Field.Selection>

          <Field.String
            defaultValue=""
            label="Description"
            maxLength={500}
            multiline
            path="/details/description"
            rows={4}
            top="medium"
            width="stretch"
          />
          <Form.Section.Toolbar>
            <FormSubmitButton disabled={!hasChanged || hasErrors()} switchContainerModeRef={switchContainerModeRef} />
            <FormCancelButton onCancel={handleCancel} switchContainerModeRef={switchContainerModeRef} />
          </Form.Section.Toolbar>
          {errorMessage && <FormStatus state="error" stretch text={errorMessage} top="small" />}
        </Form.Section.EditContainer>
      </Form.Section>
    </Form.Handler>
  );
}
